/**
 * Native Apple Sign In bridge — wraps the AppleSignInPlugin Capacitor plugin.
 *
 * On iOS, this calls ASAuthorizationController (native SIWA sheet).
 * Not available on web/desktop — callers must guard with isNativePlatform().
 *
 * IOS-SIWA-NATIVE-PLUGIN
 */
import { registerPlugin } from '@capacitor/core'

export interface AppleSignInUser {
  firstName?: string
  lastName?: string
}

export interface AppleSignInCredential {
  identityToken: string
  authorizationCode: string
  email?: string
  user?: AppleSignInUser
  /**
   * The cryptographically-random nonce the native plugin generated for this
   * request. The plugin sent SHA-256(nonce) to Apple as request.nonce; Apple
   * binds the unhashed nonce as a `nonce` claim in the identity_token JWT.
   * Send this back to the backend with /api/v1/auth/apple/native so it can
   * verify the JWT's nonce claim and defeat replay/CSRF.
   * SPEC-IOS-DEFENSE-AUDIT (#237).
   */
  nonce?: string
}

export interface AppleSignInPlugin {
  signIn(): Promise<AppleSignInCredential>
}

const AppleSignIn = registerPlugin<AppleSignInPlugin>('AppleSignIn')

/**
 * Trigger native SIWA sheet via ASAuthorizationController.
 * Returns the identity_token + authorizationCode from Apple.
 * Throws if the user cancels ('cancelled') or an error occurs.
 */
export async function nativeAppleSignIn(): Promise<AppleSignInCredential> {
  return AppleSignIn.signIn()
}
